/**
 * Scripted Iris: the levels where everything she says is written in advance
 * (docs/iris.md, "Scripted levels"; docs/agent-first-plan.md).
 *
 * On the first general levels Iris costs nothing and needs no account,
 * because nothing she says comes from a model. A level's `iris_script:` is a
 * small graph of beats. Each beat is something she says and, optionally, a
 * command she proposes or a press of Check my work. The learner answers with
 * the panel's buttons or with one of the replies the author wrote; there is no
 * free text, because nothing is listening to it.
 *
 * A script may sit on a general level of any number (docs/designed-agents.md,
 * workstream C), and a beat may say more than one line of chat: who speaks it
 * (`agent`, Iris or Kite), machine text it shows without running (`show`),
 * what it cost (`tokens`), and whether its replies are the learner's brief
 * to the agent (`ask`).
 *
 * Some beats are planted mistakes (`caught_by`), and some contradict an
 * earlier beat on purpose: agents do both, and these levels are where a
 * learner first practises noticing. The claim a scripted level makes is
 * proved, not trusted, in both directions:
 *
 * - **approving everything fails**: the path that runs every proposal
 *   (`approveAllPath`) must pass through a planted mistake and must fail the
 *   level when its commands are run on the seed;
 * - **a careful learner can finish with Iris alone**: the path that runs
 *   every proposal except the planted ones (`carefulPath`) must pass.
 *
 * scripts/check-missions.mjs and server/test/missions.test.ts run both.
 * Each planted mistake also has its own `wrong/iris-<beat id>.sh`, as a
 * checkpoint's traps do (`irisProofProblems`).
 *
 * `caught_by` is authoring-only. The build strips it with the rest of the
 * grading (scripts/build-missions.mjs), so the tab knows what Iris will say
 * but not which of it is wrong.
 */
export const IRIS_SCRIPT_AGENTS = { iris: 'Iris', kite: 'Kite' };
/** The id that ends a conversation when it is a beat's `next` or `rejected`. */
export const IRIS_SCRIPT_END = 'end';
/**
 * The last general level of the free scripted stretch. From the next one,
 * a level without an `iris_script` has the live agent, which needs an
 * account (docs/iris.md, "Modes"); a level with one is scripted whatever its
 * number (docs/designed-agents.md, workstream C).
 */
export const IRIS_SCRIPTED_THROUGH_LEVEL = 4;
/** The beat after `beat` for `move`, or null when the conversation is over. */
export function nextBeat(script, beat, move) {
    let target;
    if (move.kind === 'reply')
        target = beat.replies?.[move.index]?.next;
    else if (move.kind === 'reject')
        target = beat.rejected ?? beat.next;
    else
        target = beat.next;
    if (target === undefined) {
        const i = script.indexOf(beat);
        return i >= 0 && i + 1 < script.length ? script[i + 1] : null;
    }
    if (target === IRIS_SCRIPT_END)
        return null;
    return script.find((b) => b.id === target) ?? null;
}
/** Whether the beat proposes something the learner can approve or reject. */
export function beatProposes(beat) {
    return beat.run !== undefined || beat.check === true;
}
/**
 * The running total of a conversation's cost: the `tokens` of every message
 * on it, the agents' beats and the learner's chosen replies alike. Display
 * and data only; it grades nothing (docs/designed-agents.md).
 */
export function scriptTokens(messages) {
    return messages.reduce((sum, m) => sum + (m.tokens ?? 0), 0);
}
/**
 * The `detail` of the `agent-decision` event for one answer to a beat
 * (docs/designed-agents.md, workstream B): `<beat id>:<move>`, the move one
 * of `approve`, `reject`, `reply0` to `reply3`. Null when the answer is not a
 * decision: Go on past a beat that proposes nothing judges nothing, while a
 * reply is a choice on any beat, because a brief is one.
 */
export function decisionDetail(beat, move) {
    if (move.kind === 'reply')
        return `${beat.id}:reply${move.index}`;
    if (!beatProposes(beat))
        return null;
    return `${beat.id}:${move.kind}`;
}
/**
 * The commands one way of answering runs, in order. `rejectPlanted` is the
 * careful learner; without it, every proposal is approved. Replies are never
 * taken: they are detours an author writes back into the main line, and the
 * two proofs are about the buttons.
 */
function walk(script, rejectPlanted) {
    const commands = [];
    const planted = [];
    const seen = new Set();
    let beat = script[0] ?? null;
    while (beat && !seen.has(beat.id)) {
        seen.add(beat.id);
        const isPlanted = (beat.caught_by?.length ?? 0) > 0;
        if (isPlanted)
            planted.push(beat.id);
        const reject = rejectPlanted && isPlanted && beatProposes(beat);
        if (!reject && beat.run !== undefined)
            commands.push(beat.run);
        beat = nextBeat(script, beat, { kind: reject ? 'reject' : 'approve' });
    }
    return { commands, planted };
}
/** Every proposal approved, as a learner who reads nothing would. */
export function approveAllPath(script) {
    return walk(script, false);
}
/** Every proposal approved except the planted mistakes. */
export function carefulPath(script) {
    return { commands: walk(script, true).commands };
}
/**
 * A path's commands as a script the author tools can run on a seeded
 * workspace, from where the level's terminal starts. No `set -e`: an
 * approved command that fails does not stop the learner approving the next.
 */
export function pathScript(cwd, commands) {
    return [cwd ? `cd ${JSON.stringify(cwd)}` : '', ...commands].filter(Boolean).join('\n') + '\n';
}
const BEAT_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const KEYS = new Set(['id', 'agent', 'say', 'show', 'tokens', 'ask', 'run', 'check', 'next', 'rejected', 'replies', 'caught_by']);
const REPLY_KEYS = new Set(['say', 'next', 'tokens']);
const positiveInt = (n) => typeof n === 'number' && Number.isInteger(n) && n > 0;
/**
 * The `iris_script:` block, checked strictly, and the problems as sentences.
 * Empty when it is sound. Everything here fails in the quiet direction if it
 * slips: a `next` naming no beat ends the conversation early, and a planted
 * mistake nothing catches is one approving passes.
 */
export function irisScriptProblems(raw, objectiveIds) {
    const problems = [];
    if (!Array.isArray(raw) || raw.length === 0)
        return ['iris_script must be a non-empty list of beats'];
    const ids = new Set();
    raw.forEach((entry, i) => {
        const b = entry;
        if (typeof b?.id === 'string') {
            if (ids.has(b.id))
                problems.push(`iris_script has two beats called "${b.id}"`);
            ids.add(b.id);
        }
    });
    const target = (where, t) => {
        if (typeof t !== 'string' || (t !== IRIS_SCRIPT_END && !ids.has(t))) {
            problems.push(`${where} goes to "${String(t)}", which is not a beat on this level (or "${IRIS_SCRIPT_END}")`);
        }
    };
    raw.forEach((entry, i) => {
        const where = `iris_script[${i}]`;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
            problems.push(`${where} must be a mapping`);
            return;
        }
        const b = entry;
        for (const key of Object.keys(b))
            if (!KEYS.has(key))
                problems.push(`${where} has an unknown key "${key}"`);
        if (typeof b.id !== 'string' || !BEAT_ID.test(b.id) || b.id === IRIS_SCRIPT_END) {
            problems.push(`${where}.id must be kebab-case and not "${IRIS_SCRIPT_END}"`);
        }
        const name = typeof b.id === 'string' ? `beat "${b.id}"` : where;
        if (typeof b.say !== 'string' || b.say.trim() === '')
            problems.push(`${name} needs "say"`);
        if (b.agent !== undefined && !Object.keys(IRIS_SCRIPT_AGENTS).includes(b.agent)) {
            problems.push(`${name} "agent" must be one of ${Object.keys(IRIS_SCRIPT_AGENTS).join(', ')}, not ${JSON.stringify(b.agent)}`);
        }
        if (b.show !== undefined && (typeof b.show !== 'string' || b.show.trim() === ''))
            problems.push(`${name} "show" must be text to display`);
        if (b.tokens !== undefined && !positiveInt(b.tokens))
            problems.push(`${name} "tokens" must be a positive integer`);
        if (b.ask !== undefined) {
            if (b.ask !== true)
                problems.push(`${name} "ask" can only be true`);
            // A brief is what the learner tells an agent before it acts, so a beat
            // that already proposes something has nothing to be briefed about, and
            // one with no replies has nothing to choose from.
            if (b.run !== undefined || b.check !== undefined)
                problems.push(`${name} asks for a brief and proposes something; an "ask" beat proposes nothing`);
            if (b.replies === undefined)
                problems.push(`${name} asks for a brief but offers no replies to choose from`);
        }
        if (b.run !== undefined && (typeof b.run !== 'string' || b.run.trim() === ''))
            problems.push(`${name} "run" must be a command`);
        if (b.check !== undefined && b.check !== true)
            problems.push(`${name} "check" can only be true`);
        if (b.run !== undefined && b.check !== undefined)
            problems.push(`${name} proposes a command and a check; one beat, one proposal`);
        if (b.next !== undefined)
            target(`${name} next`, b.next);
        if (b.rejected !== undefined) {
            if (b.run === undefined && b.check === undefined)
                problems.push(`${name} has "rejected" but proposes nothing to reject`);
            target(`${name} rejected`, b.rejected);
        }
        if (b.replies !== undefined) {
            if (!Array.isArray(b.replies) || b.replies.length === 0 || b.replies.length > 4) {
                problems.push(`${name} replies must be a list of one to four`);
            }
            else {
                b.replies.forEach((r, j) => {
                    const reply = r;
                    if (typeof reply !== 'object' || reply === null || Array.isArray(reply)) {
                        problems.push(`${name} replies[${j}] must be a mapping`);
                        return;
                    }
                    for (const key of Object.keys(reply))
                        if (!REPLY_KEYS.has(key))
                            problems.push(`${name} replies[${j}] has an unknown key "${key}"`);
                    if (reply.tokens !== undefined && !positiveInt(reply.tokens))
                        problems.push(`${name} replies[${j}] "tokens" must be a positive integer`);
                    if (typeof reply?.say !== 'string' || reply.say.trim() === '' || reply.say.length > 160) {
                        problems.push(`${name} replies[${j}] needs a "say" of at most 160 characters (a brief to an agent is a sentence or two)`);
                    }
                    target(`${name} replies[${j}]`, reply?.next);
                });
            }
        }
        if (b.caught_by !== undefined) {
            if (b.run === undefined)
                problems.push(`${name} is planted but proposes no command; a planted mistake is something to approve`);
            if (!Array.isArray(b.caught_by) || b.caught_by.length === 0) {
                problems.push(`${name} caught_by must list the objectives approving it fails`);
            }
            else {
                for (const ref of b.caught_by) {
                    if (typeof ref !== 'string' || !objectiveIds.has(ref))
                        problems.push(`${name} is caught by "${String(ref)}", which is not an objective id`);
                }
            }
        }
    });
    if (problems.length > 0)
        return problems;
    const script = raw;
    // Every beat reachable from the first, by any answer.
    const reached = new Set();
    const queue = [script[0]];
    while (queue.length) {
        const beat = queue.shift();
        if (reached.has(beat.id))
            continue;
        reached.add(beat.id);
        const moves = [{ kind: 'approve' }, { kind: 'reject' }, ...(beat.replies ?? []).map((_, index) => ({ kind: 'reply', index }))];
        for (const move of moves) {
            const after = nextBeat(script, beat, move);
            if (after)
                queue.push(after);
        }
    }
    for (const b of script)
        if (!reached.has(b.id))
            problems.push(`beat "${b.id}" can never be reached`);
    if (approveAllPath(script).planted.length === 0) {
        problems.push('approving everything never meets a planted mistake; a scripted level needs one on its main line');
    }
    return problems;
}
/** The planted beats as checkpoint-style traps, for `irisProofProblems`. */
export function scriptTraps(script) {
    return (script ?? [])
        .filter((b) => (b.caught_by?.length ?? 0) > 0)
        .map((b) => ({ id: b.id, caught_by: [...b.caught_by].sort() }));
}
/** The script as the tab may see it: everything but which beats are planted. */
export function publicIrisScript(script) {
    return script.map(({ caught_by: _planted, ...beat }) => ({ ...beat, ...(beat.replies ? { replies: beat.replies.map((r) => ({ ...r })) } : {}) }));
}
//# sourceMappingURL=iris-script.js.map